<template>
  <Layout :backToRoute="true">
    <div class="primary--text section-title font-weight-bold">
      <v-icon color="primary">wysiwyg</v-icon>
      {{ $t("transactions.title") }}
    </div>
    <v-progress-circular
      v-if="loadingOrderDetails"
      indeterminate
      color="secondary"
      class="absolute-center"
    ></v-progress-circular>
    <v-card v-else elevation="0" class="secondary py-4 px-6 semi-rounded mt-12">
      <div class="d-flex flex-column primary--text font-weight-light">
        <!-- top info -->
        <div
          class="d-flex justify-center align-center font-weight-medium mb-10"
          style="font-size: 13px"
        >
          <v-btn x-small fab elevation="0" plain class="white"
            ><v-icon :class="transaction.type.toLowerCase()">{{
              transaction.settings.icon
            }}</v-icon></v-btn
          >
          <span class="pl-3">{{
            `${formattedDate}&nbsp; | &nbsp;${formattedTime}`
          }}</span>
          <div
            class="flex-grow-1 subtitle-1 font-weight-bold d-flex align-center justify-end"
            :class="transaction.type.toLowerCase()"
          >
            <span>{{ transaction.settings.symbol }}</span>
            <span>{{ transaction.settings.amount }}</span>
            <v-icon
              v-if="transaction.settings.currencies.cash"
              :class="transaction.type.toLowerCase()"
              small
              >{{ currencySymbol }}</v-icon
            >
            <span
              v-if="
                transaction.settings.currencies.cash &&
                transaction.settings.currencies.tokens
              "
              style="font-size: 23px"
              class="px-1 font-weight-bold"
              >/</span
            >
            <icon
              :name="'star'"
              v-if="transaction.settings.currencies.tokens"
              width="18"
              :class="transaction.type.toLowerCase()"
              class="mt-n1 pl-1"
            />
          </div>
        </div>
        <!-- end top info -->

        <span class="subtitle-2 font-weight-light text-center">{{
          $t("transactions.transaction_types.title")
        }}</span>
        <span class="font-weight-bold title text-center mb-4 text-uppercase">{{
          $t(`transactions.transaction_types.${transaction.type}`)
        }}</span>
        <div v-if="transactionOrders.station" class="d-flex flex-column">
          <span class="subtitle-2 font-weight-light mt-4">{{
            $t("transactions.station")
          }}</span>
          <span class="font-weight-bold subtitle-1 my-2">{{
            transactionOrders.station.name
          }}</span>
        </div>
        <div v-if="transaction.concept" class="d-flex flex-column">
          <span class="subtitle-2 font-weight-light mt-4">{{
            $t("payment.payment_method")
          }}</span>
          <span class="font-weight-bold subtitle-1 mt-2">{{
            transaction.concept
          }}</span>
        </div>

        <!-- New Order Detail -->
        <div
          v-if="
            transaction.type === transactionTypes.ORDER && transactionOrders
          "
          class="mt-6 mb-2"
        >
          <v-row class="mb-2">
            <v-col cols="6" class="pr-0"
              ><span class="body-2">{{
                $t("transactions.product")
              }}</span></v-col
            >
            <v-col cols="2" class="text-end pr-0"
              ><span class="body-2">{{
                $t("transactions.quantity_short")
              }}</span></v-col
            >
            <v-col cols="4" class="text-end pr-2"
              ><span class="body-2">{{ $t("transactions.price") }}</span></v-col
            >
          </v-row>
          <div v-for="orderLine in decoratedTransactions" :key="orderLine.id">
            <div
              class="pb-4"
              v-if="
                orderLine.item_entity !== 'TAX' &&
                !orderLine.order_line_invitations.length
              "
            >
              <v-row>
                <v-col cols="6" class="pr-0">
                  <span class="font-weight-medium">
                    {{ orderLine.name }}
                  </span>
                </v-col>
                <v-col cols="2" class="text-end pr-0"
                  ><span class="font-weight-medium">{{
                    orderLine.quantity
                  }}</span></v-col
                >
                <v-col cols="4" class="text-end pr-1">
                  <span class="font-weight-medium">
                    {{
                      orderLine.price < 0
                        ? "-" +
                          amountWithSeparator(
                            orderLine.quantity * orderLine.price * -1,
                            $t("decimal_separator")
                          )
                        : amountWithSeparator(
                            orderLine.quantity * orderLine.price,
                            $t("decimal_separator")
                          )
                    }}
                    <v-icon color="primary" small class="mt-n1"
                      >{{ currencySymbol }}
                    </v-icon>
                  </span>
                </v-col>
              </v-row>
              <div v-if="orderLine.options.length">
                <v-row v-for="option in orderLine.options" :key="option.id">
                  <v-col cols="6" class="pl-5">
                    <span>&bull;{{ option.name }}</span>
                  </v-col>
                  <v-col cols="2" class="text-end pr-0 font-weight-medium">{{
                    option.quantity
                  }}</v-col>
                  <v-col cols="4" class="text-end pr-1 font-weight-medium"
                    >{{
                      option.price < 0
                        ? "-" +
                          amountWithSeparator(
                            option.quantity * option.price * -1,
                            $t("decimal_separator")
                          )
                        : amountWithSeparator(
                            option.quantity * option.price,
                            $t("decimal_separator")
                          )
                    }}
                    <v-icon color="primary" small class="mt-n1"
                      >{{ currencySymbol }}
                    </v-icon>
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
          <!-- tip and discounts -->
          <br />
          <div v-for="(discount, i) in discountsAndTip" :key="i">
            <div
              class="pb-4"
              v-if="
                discount.item_entity !== 'TAX' &&
                i !== itemEntities.TIP.toLowerCase()
              "
            >
              <v-row>
                <v-col cols="8" class="pr-0 text-right">
                  <span class="font-weight-medium">
                    <span v-if="discount.value" class="px-3"
                      >({{ discount.value }}%)</span
                    >
                    {{ discount.name }}
                  </span>
                </v-col>
                <v-col cols="4" class="text-end pr-1">
                  <span class="font-weight-medium">
                    {{
                      discount.price < 0
                        ? "-" +
                          amountWithSeparator(
                            discount.total * discount.qty * -1,
                            $t("decimal_separator")
                          )
                        : amountWithSeparator(
                            discount.total * discount.qty,
                            $t("decimal_separator")
                          )
                    }}
                    <v-icon color="primary" small class="mt-n1"
                      >{{ currencySymbol }}
                    </v-icon>
                  </span>
                </v-col>
              </v-row>
            </div>
          </div>
          <div class="pb-4" v-if="discountsAndTip.tip">
            <v-row>
              <v-col cols="8" class="pr-0 text-right">
                <span class="font-weight-medium">
                  <v-icon
                    class="pr-1"
                    style="font-size: 18px; color: var(--primary-color)"
                    >monetization_on</v-icon
                  >
                  {{ discountsAndTip.tip.name || "TIP" }}
                </span>
              </v-col>
              <v-col cols="4" class="text-end pr-1">
                <span class="font-weight-medium">
                  {{
                    amountWithSeparator(
                      discountsAndTip.tip.total,
                      $t("decimal_separator")
                    )
                  }}
                  <v-icon color="primary" small class="mt-n1"
                    >{{ currencySymbol }}
                  </v-icon>
                </span>
              </v-col>
            </v-row>
          </div>
        </div>
        <!-- End New Order Detail -->

        <div style="border: 1px solid white" class="mt-4 mb-4"></div>

        <!--Footer-->
        <div
          v-if="
            transaction.type !== transactionTypes.TOKEN &&
            transaction.settings.currencies.cash
          "
          class="d-flex mb-2 justify-space-between align-baseline"
        >
          <span class="subtitle-2 text-uppercase font-weight-bold">{{
            $t("transactions.total")
          }}</span>
          <div class="title font-weight-bold">
            <span
              class="pr-1"
              v-if="
                transaction.type === transactionTypes.ORDER_REFUND ||
                transaction.type === transactionTypes.RECHARGE_REFUND
              "
              >{{ transaction.settings.symbol }}</span
            >
            <span>{{ transaction.settings.amount }}</span>
            <v-icon
              v-if="transaction.settings.currencies.cash"
              color="primary"
              small
              >{{ currencySymbol }}</v-icon
            >
          </div>
        </div>

        <div
          v-if="transaction.type === transactionTypes.ORDER"
          class="mb-2"
          :class="
            transaction.settings.currencies.tokens &&
            !transaction.settings.currencies.cash
              ? 'mt-6 '
              : ''
          "
        >
          <div
            v-if="
              transaction.settings.currencies.tokens &&
              transaction.settings.currencies.cash
            "
            style="border: 1px solid white"
            class="mt-2 mb-6"
          ></div>
          <div
            v-for="orderLine in transactionOrders.order_lines"
            :key="orderLine.id"
          >
            <div
              v-if="orderLine.order_line_invitations.length"
              class="d-flex font-weight-medium justify-space-between my-4"
            >
              <div class="d-flex">
                <icon
                  :name="
                    orderLine.order_line_invitations[0].token
                      ? orderLine.order_line_invitations[0].token.icon
                      : 'star'
                  "
                  width="30"
                  class="mr-4"
                ></icon>
                <span class="pt-1">X{{ orderLine.quantity }}</span>
              </div>
              <div class="px-4">{{ orderLine.name }}</div>
              <div class="nowrap">
                {{ getTokensByOrderLine(orderLine.order_line_invitations) }}
                <icon
                  :name="'star'"
                  width="16"
                  :color="$vuetify.theme.themes.light.primary"
                />
              </div>
            </div>
          </div>
          <div
            v-if="transaction.settings.currencies.tokens"
            style="border: 1px solid white"
            class="mt-2 mb-2"
          ></div>
        </div>

        <div
          v-if="
            transaction.type === transactionTypes.TOKEN ||
            transaction.type === transactionTypes.ORDER ||
            (transaction.type === transactionTypes.ORDER_REFUND && totalTokens)
          "
          class="d-flex pb-6 justify-space-between align-baseline"
        >
          <span class="subtitle-2 text-uppercase font-weight-bold">{{
            $t("transactions.total_tokens")
          }}</span>
          <div class="title font-weight-bold">
            <span v-if="transaction.type === transactionTypes.TOKEN">{{
              transaction.amount
            }}</span>
            <span v-else>{{ totalTokens }}</span>
            <icon
              :name="'star'"
              v-if="transaction.settings.currencies.tokens"
              width="22"
              :color="this.$vuetify.theme.themes.light.primary"
              class="mt-n1 pl-1"
            />
          </div>
        </div>
        <!-- End footer -->
      </div>
    </v-card>
  </Layout>
</template>
<script>
import Layout from "@/views/layouts/MainLayout.vue";
import { amountWithSeparator } from "@/utils/MoneyUtils";
import { mapState } from "vuex";
import {
  transactionTypes,
  itemEntities,
} from "@/entities/app-data/Transaction";
import { RequestService } from "../services/request.service";
import errorManager from "@/services/error.manager";

const requestService = new RequestService();

export default {
  props: {
    transaction: {
      type: Object,
    },
  },
  data() {
    return {
      transactionTypes,
      itemEntities,
      transactionOrders: [],
      totalTokens: 0,
      loadingOrderDetails: true,
      decoratedTransactions: [],
      discountsAndTip: null,
    };
  },
  components: { Layout },
  computed: {
    ...mapState({
      currencySymbol: (state) =>
        state.customization.customization.economics.currency_symbol,
      stations: (state) => state.customization.customization.stations,
    }),
    formattedDate() {
      const date = moment(this.transaction.moment).locale(navigator.language);
      return date.format("D MMMM");
    },
    formattedTime() {
      const date = moment(this.transaction.moment).locale(navigator.language);
      return date.format("hh:mm");
    },
  },
  methods: {
    amountWithSeparator,
    getTokensByOrderLine(tokens) {
      return tokens.reduce((total, token) => total + token.quantity, 0);
    },
    async getOrderInfo() {
      const context = this;
      context.discountsAndTip = {};
      await requestService
        .getOrderByAnyId(context, this.transaction.id)
        .then((order) => {
          this.loadingOrderDetails = false;
          context.transactionOrders = order
            ? order
            : {
                station: {
                  name: context.getDecoratedStation(context.transaction.origin),
                },
              };
          let decoratedTransactions = order.order_lines.filter(
            (transaction) =>
              !transaction.parent_order_line_id &&
              transaction.item_entity !== context.itemEntities.TIP
          );
          decoratedTransactions.map((transaction) => {
            let options = [];
            order.order_lines.map((option) => {
              if (option.item_entity === context.itemEntities.TIP)
                context.discountsAndTip.tip = {
                  name: option.name,
                  total: option.price,
                };
              if (option.parent_order_line_id == transaction.id) {
                if (
                  option.item_entity !== context.itemEntities.DISCOUNT &&
                  option.item_entity !== context.itemEntities.TIP
                )
                  options.push(option);
                else {
                  if (
                    typeof this.discountsAndTip[option.item_id] !== "undefined"
                  ) {
                    this.discountsAndTip[option.item_id].total += option.price;
                  } else {
                    this.discountsAndTip[option.item_id] = {
                      name: option.name,
                      total: option.price,
                      qty: option.quantity,
                      value: option.value,
                    };
                  }
                }
              }
            });
            transaction.options = options || [];
            //set token icon
            transaction.order_line_invitations.map((token) => {
              token.token = this.$store.getters.eventTokens.find((t) => {
                return t.id === token.invitation_id;
              });
            });
            return transaction;
          });
          this.decoratedTransactions = decoratedTransactions;
        })
        .catch((error) => {
          errorManager.manageError(context, error);
        });
    },
    getDecoratedStation(id) {
      let station = this.stations.find((station) => station.id == id);
      return station ? station.name : id;
    },
    getTotalTokens() {
      if (this.transaction.type === this.transactionTypes.ORDER) {
        this.totalTokens = this.transaction.token_transactions.reduce(
          (total, token) => {
            return total + token.amount;
          },
          0
        );
      }
    },
  },
  mounted() {
    this.getOrderInfo();
    this.getTotalTokens();
  },
};
</script>
